/**
 * LeanManagement.jsx — Nirvikalp 4.0
 *
 * Value Stream Mapping / Lean analytics — REPORTING layer, kept fully
 * separate from ScanModule.jsx (which handles execution: cut plan, lays,
 * barcodes, scanning). This file reads the same pmDb data ScanModule writes
 * to, but has no dependency on ScanModule's internals — it's self-contained
 * so execution and reporting code never get tangled together.
 *
 * Scope: PRODUCTION FLOOR NVA only (machine breakdown, material shortage,
 * changeover, rework, etc. — everything capturable through scan timestamps
 * and lost-time tickets). Organizational NVA (approval delays, cross-
 * department handoffs) is a separate future scope — it can't be barcode-
 * scanned, so it needs a different data-capture mechanism entirely.
 *
 * All numbers here are deterministic (formula/statistics). AI is used only
 * to narrate already-computed numbers into prose — never to produce them.
 *
 * Props: { db, pmDb, setPmDb, apiLive }  — same shape ScanModule receives.
 *
 * Mount as a new top-level tab in ProductionMonitor.jsx, e.g.:
 *   import LeanManagement from './LeanManagement';
 *   ...
 *   { tab==='lean' && <LeanManagement db={db} pmDb={pmDb} setPmDb={setPmDb} apiLive={apiLive} /> }
 */

import React, { useState, useMemo, useCallback } from 'react';

const PM_API = 'https://nirvikalp.integrashion.com/api/pm_api.php';

const LS = {
  get: k  => { try { return JSON.parse(localStorage.getItem(`pm_${k}`)) || null; } catch { return null; } },
  set: (k,v) => { try { localStorage.setItem(`pm_${k}`, JSON.stringify(v)); } catch {} },
};

const pmApi = {
  async save(table, data) {
    try {
      const r = await fetch(PM_API, { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'save', table, data }) });
      return await r.json();
    } catch { return { success:false }; }
  },
  async call(action, payload={}) {
    try {
      const r = await fetch(PM_API, { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action, ...payload }) });
      return await r.json();
    } catch (e) { return { success:false, error:String(e) }; }
  },
};

// Minimal line-list resolver — same shape as ScanModule's bridge.getAllLines,
// duplicated on purpose so this file has zero import dependency on ScanModule.
function getAllLines(db) {
  const factories = (db?.suppliers||[]).filter(s => s.supplierType==='Garment Supplier');
  const out = [];
  factories.forEach(f => {
    const lines = f.sewingLines || f.lines || f.productionLines || f.sewLines || [];
    lines.forEach(l => out.push({ ...l, factoryName:f.name, factoryId:f.id }));
  });
  return out;
}

// ── Waste classification — which lost-time reasons are "pure waste"
// (attack first) vs "necessary NVA" (required, minimize but won't
// disappear). Standard lean classification applied to the L-codes.
const WASTE_CLASS = {
  L1:'PURE_WASTE', L2:'PURE_WASTE', L3:'PURE_WASTE', L8:'PURE_WASTE',
  L4:'NECESSARY_NVA', L5:'NECESSARY_NVA', L6:'NECESSARY_NVA', L7:'NECESSARY_NVA',
  L9:'NECESSARY_NVA', L10:'NECESSARY_NVA', L11:'NECESSARY_NVA', L12:'NECESSARY_NVA',
};
const classifyWaste = (code) => WASTE_CLASS[code] || 'NECESSARY_NVA';

// Cost model — derived once from real reference points:
//   6 cents/SMV-min at 55% efficiency → 3.3 cents/SMV-min at 100% efficiency
//   $850/day for a 65-machine line → $0.0233/worker/minute idle cost
// Both editable below, since labor rates change over time/factory.
const DEFAULT_VSM_CONFIG = {
  smvCostAt100Pct: 0.033,
  idleCostPerWorkerMin: 0.0233,
  availMinPerDay: 560,
};

// Garment-type headcount reference — LAST RESORT fallback only, used when
// neither actual roster nor SMV/Takt calculation is available.
const GARMENT_HEADCOUNT_REF = [
  { keywords:['shirt'],    workers:52  },
  { keywords:['trouser','pant'], workers:72 },
  { keywords:['cargo'],    workers:105 },
];

// ── Stats card row (small, self-contained copy) ──────────────────────────────
function Stats({ items }) {
  return (
    <div className="lm-stats">
      {items.map(s => (
        <div key={s.label} className="lm-stat-b">
          <div className="lm-stat-l">{s.label}</div>
          <div className="lm-stat-v" style={s.color?{color:s.color}:{}}>{s.val??'—'}</div>
          {s.sub && <div className="lm-stat-s">{s.sub}</div>}
        </div>
      ))}
    </div>
  );
}

export default function LeanManagement({ db, pmDb, setPmDb, apiLive }) {
  const [subTab, setSubTab] = useState('dashboard'); // 'dashboard' | 'config'

  const allLines = useMemo(() => getAllLines(db), [db]);

  // ── VSM cost config — editable, persisted, falls back to derived defaults ─
  const vsmConfig = { ...DEFAULT_VSM_CONFIG, ...(pmDb.vsmConfig||{}) };
  const saveVsmConfig = async (cfg) => {
    const upd = {...pmDb, vsmConfig:cfg};
    setPmDb(upd); LS.set('vsmConfig', cfg);
    if (apiLive) await pmApi.save('pm_vsm_config', cfg);
  };

  // Headcount resolution — priority: actual roster > SMV/Takt calc > reference table
  const resolveLineHeadcount = useCallback((lineId, styleRefForLine) => {
    const roster = (pmDb.workers||[]).filter(w=>w.line_id===lineId);
    if (roster.length) return { count: roster.length, source: 'roster' };

    const plan = (pmDb.cutPlans||[]).find(p => p.style_ref===styleRefForLine);
    const lineCfg = (pmDb.lineConfigs||[]).find(l=>l.line_id===lineId);
    const dailyTarget = parseInt(lineCfg?.daily_target || lineCfg?.workers_count) || 0;
    if (plan?.total_smv && dailyTarget) {
      const taktTime = vsmConfig.availMinPerDay / dailyTarget;
      const estCount = Math.ceil(plan.total_smv / taktTime);
      if (estCount > 0) return { count: estCount, source: 'smv_takt_estimate' };
    }

    const nameToMatch = (styleRefForLine||'').toLowerCase();
    const ref = GARMENT_HEADCOUNT_REF.find(r => r.keywords.some(k=>nameToMatch.includes(k)));
    if (ref) return { count: ref.workers, source: 'reference_table' };

    return { count: 0, source: 'unknown' };
  }, [pmDb.workers, pmDb.cutPlans, pmDb.lineConfigs, vsmConfig.availMinPerDay]);

  // ── Filters ──────────────────────────────────────────────────────────────
  const [vsmDateFrom, setVsmDateFrom] = useState(()=>{
    const d = new Date(); d.setDate(d.getDate()-7);
    return d.toISOString().slice(0,10);
  });
  const [vsmDateTo, setVsmDateTo] = useState(new Date().toISOString().slice(0,10));
  const [vsmLineFilter, setVsmLineFilter] = useState('');

  // ── Core computation engine — everything deterministic ────────────────────
  const vsmStats = useMemo(() => {
    const inRange = (dateStr) => dateStr >= vsmDateFrom && dateStr <= vsmDateTo;
    const lineMatch = (lid) => !vsmLineFilter || lid === vsmLineFilter;

    const vaRecords = (pmDb.scanRecords||[]).filter(r =>
      inRange((r.date||r.scanned_at||'').slice(0,10)) && lineMatch(r.line_id));
    const totalVAmin = vaRecords.reduce((s,r)=>s+(parseFloat(r.smv_earned)||0), 0);

    const nvaRecords = (pmDb.lostTimeRecords||[]).filter(r =>
      inRange(r.date) && lineMatch(r.line_id));
    const totalNVAmin = nvaRecords.reduce((s,r)=>s+(parseInt(r.duration_min)||0), 0);
    const pureWasteMin = nvaRecords.filter(r=>classifyWaste(r.reason_code)==='PURE_WASTE')
      .reduce((s,r)=>s+(parseInt(r.duration_min)||0), 0);
    const necessaryNVAmin = totalNVAmin - pureWasteMin;

    const pceFloor = (totalVAmin+totalNVAmin) > 0
      ? Math.round((totalVAmin/(totalVAmin+totalNVAmin))*100) : 0;

    const relevantGarmentScans = (pmDb.garmentScans||[]).filter(s =>
      inRange(s.date) && lineMatch(s.line_id));
    const lineInMap = {}; relevantGarmentScans.filter(s=>s.checkpoint==='LINE_IN').forEach(s=>lineInMap[s.barcode]=s);
    const leadTimes = relevantGarmentScans.filter(s=>s.checkpoint==='FINISHING_OUT' && lineInMap[s.barcode])
      .map(s => (new Date(s.scanned_at) - new Date(lineInMap[s.barcode].scanned_at)) / 60000);
    const avgLeadTimeMin = leadTimes.length ? leadTimes.reduce((a,b)=>a+b,0)/leadTimes.length : null;
    const pcePrecise = (avgLeadTimeMin && leadTimes.length)
      ? Math.round((totalVAmin / (avgLeadTimeMin * leadTimes.length)) * 100) : null;

    const byReason = {};
    nvaRecords.forEach(r => {
      const key = r.reason_code;
      if (!byReason[key]) byReason[key] = {
        code:key, label:r.reason_label, minutes:0, idleCost:0, records:0,
        wasteClass: classifyWaste(key),
      };
      byReason[key].minutes += (parseInt(r.duration_min)||0);
      byReason[key].idleCost += (parseInt(r.duration_min)||0) * vsmConfig.idleCostPerWorkerMin;
      byReason[key].records += 1;
    });

    const lineIds = [...new Set(nvaRecords.map(r=>r.line_id).filter(Boolean))];
    let totalOpportunityCost = 0;
    lineIds.forEach(lid => {
      const lineVA = vaRecords.filter(r=>r.line_id===lid).reduce((s,r)=>s+(parseFloat(r.smv_earned)||0),0);
      const lineNVA = nvaRecords.filter(r=>r.line_id===lid).reduce((s,r)=>s+(parseInt(r.duration_min)||0),0);
      const { count: headcount } = resolveLineHeadcount(lid,
        vaRecords.find(r=>r.line_id===lid)?.style_ref || '');
      const availTotal = vsmConfig.availMinPerDay * Math.max(1, headcount) *
        (Math.max(1, (new Date(vsmDateTo)-new Date(vsmDateFrom))/86400000 + 1));
      const actualEff = availTotal > 0 ? Math.min(2, lineVA / availTotal) : 0.55;
      const effForRate = actualEff > 0.05 ? actualEff : 0.55;
      const smvCostPerMin = vsmConfig.smvCostAt100Pct / effForRate;
      totalOpportunityCost += lineNVA * smvCostPerMin;
    });

    const rangeDays = Math.max(1, Math.round((new Date(vsmDateTo)-new Date(vsmDateFrom))/86400000)+1);
    const priorFrom = new Date(vsmDateFrom); priorFrom.setDate(priorFrom.getDate()-rangeDays);
    const priorTo = new Date(vsmDateFrom); priorTo.setDate(priorTo.getDate()-1);
    const priorFromStr = priorFrom.toISOString().slice(0,10);
    const priorToStr = priorTo.toISOString().slice(0,10);
    const priorNVA = (pmDb.lostTimeRecords||[]).filter(r =>
      r.date >= priorFromStr && r.date <= priorToStr && lineMatch(r.line_id));
    const priorByReason = {};
    priorNVA.forEach(r => {
      priorByReason[r.reason_code] = (priorByReason[r.reason_code]||0) + (parseInt(r.duration_min)||0);
    });

    const reasonList = Object.values(byReason).sort((a,b)=>b.idleCost-a.idleCost);
    const totalCost = reasonList.reduce((s,r)=>s+r.idleCost,0);
    let cumCost = 0;
    const rankedReasons = reasonList.map(r => {
      cumCost += r.idleCost;
      const cumPct = totalCost>0 ? (cumCost/totalCost)*100 : 0;
      const priorMin = priorByReason[r.code];
      let trend, trendPct;
      if (priorMin === undefined) { trend='NEW'; trendPct=null; }
      else if (priorMin === 0) { trend = r.minutes>0 ? 'WORSENING' : 'FLAT'; trendPct=null; }
      else { trendPct = Math.round(((r.minutes-priorMin)/priorMin)*100); trend = trendPct>10?'WORSENING':trendPct<-10?'IMPROVING':'FLAT'; }

      const inVitalFew = cumPct <= 80;
      let tier;
      if (inVitalFew && (trend==='WORSENING'||trend==='NEW')) tier = 'IMMEDIATE';
      else if (inVitalFew) tier = 'MEDIUM';
      else if (trend==='WORSENING' && trendPct>20) tier = 'MEDIUM';
      else tier = 'LOW';

      return { ...r, cumPct:Math.round(cumPct), trend, trendPct, tier };
    });

    return {
      totalVAmin: Math.round(totalVAmin), totalNVAmin, pureWasteMin, necessaryNVAmin,
      pceFloor, pcePrecise, avgLeadTimeMin: avgLeadTimeMin?Math.round(avgLeadTimeMin):null,
      leadTimeSampleSize: leadTimes.length,
      rankedReasons, totalIdleCost: Math.round(totalCost*100)/100,
      totalOpportunityCost: Math.round(totalOpportunityCost*100)/100,
      totalCost: Math.round((totalCost+totalOpportunityCost)*100)/100,
      immediateCount: rankedReasons.filter(r=>r.tier==='IMMEDIATE').length,
      mediumCount: rankedReasons.filter(r=>r.tier==='MEDIUM').length,
      lowCount: rankedReasons.filter(r=>r.tier==='LOW').length,
    };
  }, [pmDb.scanRecords, pmDb.lostTimeRecords, pmDb.garmentScans, vsmDateFrom, vsmDateTo, vsmLineFilter, vsmConfig, resolveLineHeadcount]);

  const [vsmNarrative, setVsmNarrative] = useState('');
  const [vsmNarrativeLoading, setVsmNarrativeLoading] = useState(false);
  const generateVsmNarrative = async () => {
    setVsmNarrativeLoading(true);
    const res = await pmApi.call('get_vsm_narrative', { stats: vsmStats });
    setVsmNarrative(res?.narrative || 'AI narrative requires the get_vsm_narrative action to be added to pm_api.php.');
    setVsmNarrativeLoading(false);
  };

  return (
    <>
      <style>{lmCSS}</style>
      <div className="lm-wrap">
        <div className="lm-header">
          <div>
            <div className="lm-title">📊 Lean Management</div>
            <div className="lm-subtitle">Value Stream Mapping — production floor only</div>
          </div>
          <div className="lm-subtabs">
            <button className={`lm-subtab ${subTab==='dashboard'?'on':''}`} onClick={()=>setSubTab('dashboard')}>Dashboard</button>
            <button className={`lm-subtab ${subTab==='config'?'on':''}`} onClick={()=>setSubTab('config')}>Cost Config</button>
          </div>
        </div>

        {subTab==='dashboard' && (
          <div className="lm-card">
            <div style={{fontSize:12,color:'#6C757D',marginBottom:14}}>
              Covers NVA capturable through scan events and lost-time tickets on the
              production floor. Organizational NVA (approvals, cross-department
              handoffs) is a separate future scope, tracked differently since it
              can't be barcode-scanned.
            </div>

            <div className="lm-form-row">
              <div className="lm-fg">
                <span className="lm-fl">From</span>
                <input type="date" className="lm-inp" value={vsmDateFrom} onChange={e=>setVsmDateFrom(e.target.value)}/>
              </div>
              <div className="lm-fg">
                <span className="lm-fl">To</span>
                <input type="date" className="lm-inp" value={vsmDateTo} onChange={e=>setVsmDateTo(e.target.value)}/>
              </div>
              <div className="lm-fg">
                <span className="lm-fl">Line</span>
                <select className="lm-sel" value={vsmLineFilter} onChange={e=>setVsmLineFilter(e.target.value)}>
                  <option value="">All Lines</option>
                  {allLines.map(l=>(
                    <option key={`${l.factoryId}-${l.id}`} value={l.id}>{l.factoryName} · {l.name||`Line ${l.id}`}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{display:'flex',gap:16,marginBottom:16}}>
              <div style={{flex:1,textAlign:'center',padding:'16px',background:'#F8F9FA',borderRadius:8,border:'1px solid #DEE2E6'}}>
                <div style={{fontSize:10,color:'#6C757D',textTransform:'uppercase',letterSpacing:.4}}>Process Cycle Efficiency</div>
                <div style={{fontSize:32,fontWeight:800,
                  color: vsmStats.pceFloor>=40?'#388E3C':vsmStats.pceFloor>=25?'#E65100':'#C62828'}}>
                  {vsmStats.pceFloor}%
                </div>
                <div style={{fontSize:10,color:'#ADB5BD'}}>VA ÷ (VA+NVA) — floor-level</div>
                {vsmStats.pcePrecise!==null && (
                  <div style={{fontSize:11,color:'#6A1B9A',marginTop:6,fontWeight:600}}>
                    Precise (lead-time based): {vsmStats.pcePrecise}%
                    <div style={{fontSize:9,color:'#ADB5BD',fontWeight:400}}>
                      from {vsmStats.leadTimeSampleSize} garment units, avg lead time {vsmStats.avgLeadTimeMin}min
                    </div>
                  </div>
                )}
              </div>
              <div style={{flex:2,padding:'16px',background:'#F8F9FA',borderRadius:8,border:'1px solid #DEE2E6'}}>
                <div style={{fontSize:10,color:'#6C757D',textTransform:'uppercase',letterSpacing:.4,marginBottom:8}}>Time Split</div>
                <div style={{display:'flex',height:28,borderRadius:6,overflow:'hidden'}}>
                  <div style={{width:`${vsmStats.pceFloor}%`,background:'#388E3C',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:10,fontWeight:700}}>
                    {vsmStats.pceFloor>10 && `VA ${vsmStats.totalVAmin}m`}
                  </div>
                  <div style={{width:`${100-vsmStats.pceFloor}%`,background:'#C62828',display:'flex'}}>
                    <div style={{width:`${vsmStats.totalNVAmin>0?(vsmStats.pureWasteMin/vsmStats.totalNVAmin)*100:0}%`,background:'#C62828'}}/>
                    <div style={{width:`${vsmStats.totalNVAmin>0?(vsmStats.necessaryNVAmin/vsmStats.totalNVAmin)*100:0}%`,background:'#FF8F00'}}/>
                  </div>
                </div>
                <div style={{display:'flex',gap:14,marginTop:8,fontSize:11}}>
                  <span><span style={{display:'inline-block',width:9,height:9,background:'#388E3C',borderRadius:2,marginRight:4}}/>VA: {vsmStats.totalVAmin}min</span>
                  <span><span style={{display:'inline-block',width:9,height:9,background:'#C62828',borderRadius:2,marginRight:4}}/>Pure waste: {vsmStats.pureWasteMin}min</span>
                  <span><span style={{display:'inline-block',width:9,height:9,background:'#FF8F00',borderRadius:2,marginRight:4}}/>Necessary NVA: {vsmStats.necessaryNVAmin}min</span>
                </div>
              </div>
            </div>

            <Stats items={[
              {label:'Idle/Direct Cost',  val:'$'+vsmStats.totalIdleCost.toFixed(2), color:'#C62828', sub:'wages paid, no output'},
              {label:'Opportunity Cost',  val:'$'+vsmStats.totalOpportunityCost.toFixed(2), color:'#E65100', sub:'production value lost'},
              {label:'Total Cost Impact', val:'$'+vsmStats.totalCost.toFixed(2), color:'#0D1B2A'},
              {label:'Threat Split',      val:`${vsmStats.immediateCount} / ${vsmStats.mediumCount} / ${vsmStats.lowCount}`, sub:'Immediate / Medium / Low'},
            ]}/>

            <div className="lm-sec-hdr">NVA Reasons — Ranked by Cost Impact</div>
            {vsmStats.rankedReasons.length===0 ? (
              <div className="lm-empty">No lost time recorded in this range</div>
            ) : (
              <table className="lm-tbl">
                <thead>
                  <tr>
                    <th>Threat</th><th>Reason</th><th>Class</th>
                    <th style={{textAlign:'right'}}>Minutes</th>
                    <th style={{textAlign:'right'}}>Idle Cost</th>
                    <th style={{textAlign:'right'}}>Cum%</th>
                    <th>Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {vsmStats.rankedReasons.map(r=>(
                    <tr key={r.code}>
                      <td>
                        <span className="lm-pill" style={{
                          background: r.tier==='IMMEDIATE'?'#FFCDD2':r.tier==='MEDIUM'?'#FFE082':'#F4F6F8',
                          color: r.tier==='IMMEDIATE'?'#C62828':r.tier==='MEDIUM'?'#E65100':'#6C757D',
                        }}>{r.tier}</span>
                      </td>
                      <td><strong>{r.code}</strong> {r.label}</td>
                      <td style={{fontSize:11,color: r.wasteClass==='PURE_WASTE'?'#C62828':'#ADB5BD'}}>
                        {r.wasteClass==='PURE_WASTE'?'Pure waste':'Necessary NVA'}
                      </td>
                      <td style={{textAlign:'right',fontFamily:'monospace'}}>{r.minutes}</td>
                      <td style={{textAlign:'right',fontFamily:'monospace',fontWeight:700}}>${r.idleCost.toFixed(2)}</td>
                      <td style={{textAlign:'right',fontSize:11,color:'#ADB5BD'}}>{r.cumPct}%</td>
                      <td style={{fontSize:11}}>
                        {r.trend==='WORSENING' && <span style={{color:'#C62828'}}>▲ Worsening{r.trendPct!==null&&` (${r.trendPct>0?'+':''}${r.trendPct}%)`}</span>}
                        {r.trend==='IMPROVING' && <span style={{color:'#388E3C'}}>▼ Improving ({r.trendPct}%)</span>}
                        {r.trend==='FLAT' && <span style={{color:'#ADB5BD'}}>— Flat</span>}
                        {r.trend==='NEW' && <span style={{color:'#6366f1'}}>● New</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="lm-sec-hdr" style={{marginTop:16}}>AI Summary</div>
            <button className="lm-btn lm-btn-p" onClick={generateVsmNarrative} disabled={vsmNarrativeLoading}>
              {vsmNarrativeLoading ? 'Generating…' : '✨ Generate Management Summary'}
            </button>
            {vsmNarrative && (
              <div style={{marginTop:10,padding:'12px 14px',background:'#eef2ff',border:'1px solid #c7d2fe',
                borderRadius:8,fontSize:13,color:'#3730a3',lineHeight:1.6}}>
                {vsmNarrative}
              </div>
            )}

            <div style={{marginTop:16,fontSize:10,color:'#ADB5BD',borderTop:'1px solid #DEE2E6',paddingTop:10}}>
              Cost model: ${vsmConfig.idleCostPerWorkerMin}/worker/min idle ·
              ${vsmConfig.smvCostAt100Pct}/SMV-min at 100% efficiency ·
              editable in Cost Config tab.
            </div>
          </div>
        )}

        {subTab==='config' && (
          <div className="lm-card">
            <div className="lm-card-title">💲 VSM Cost Model</div>
            <div style={{fontSize:12,color:'#6C757D',marginBottom:12}}>
              Derived from your labor rate reference points. Edit if rates change —
              every VSM calculation uses these live, nothing is hardcoded elsewhere.
            </div>
            <div className="lm-form-row">
              <div className="lm-fg">
                <span className="lm-fl">Idle cost ($/worker/min)</span>
                <input type="number" step="0.0001" className="lm-inp" style={{width:120}}
                  value={vsmConfig.idleCostPerWorkerMin}
                  onChange={e=>saveVsmConfig({...vsmConfig, idleCostPerWorkerMin:parseFloat(e.target.value)||0})}/>
              </div>
              <div className="lm-fg">
                <span className="lm-fl">SMV cost at 100% eff ($/min)</span>
                <input type="number" step="0.0001" className="lm-inp" style={{width:120}}
                  value={vsmConfig.smvCostAt100Pct}
                  onChange={e=>saveVsmConfig({...vsmConfig, smvCostAt100Pct:parseFloat(e.target.value)||0})}/>
              </div>
              <div className="lm-fg">
                <span className="lm-fl">Available min/day</span>
                <input type="number" className="lm-inp" style={{width:100}}
                  value={vsmConfig.availMinPerDay}
                  onChange={e=>saveVsmConfig({...vsmConfig, availMinPerDay:parseInt(e.target.value)||560})}/>
              </div>
            </div>
            <div style={{fontSize:11,color:'#92400e',background:'#FFF8E1',padding:'8px 10px',borderRadius:6,marginTop:8}}>
              Current derivation: $850/day ÷ 65 workers ÷ 560 min = ${DEFAULT_VSM_CONFIG.idleCostPerWorkerMin}/worker/min ·
              6¢ at 55% eff reverse-calculated to {DEFAULT_VSM_CONFIG.smvCostAt100Pct}/SMV-min at 100%
            </div>
          </div>
        )}
      </div>
    </>
  );
}

const lmCSS = `
.lm-wrap { font-family:'Inter',sans-serif; padding:16px 20px; background:#f0f2f5; min-height:100%; }
.lm-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:16px; flex-wrap:wrap; gap:10px; }
.lm-title { font-size:18px; font-weight:800; color:#0D1B2A; }
.lm-subtitle { font-size:12px; color:#6C757D; margin-top:2px; }
.lm-subtabs { display:flex; gap:4px; }
.lm-subtab { padding:7px 14px; border-radius:7px; border:1px solid #DEE2E6; background:#fff; color:#6C757D; font-size:12px; font-weight:700; cursor:pointer; }
.lm-subtab.on { background:#1A2A3A; color:#fff; border-color:#1A2A3A; }
.lm-card { background:#fff; border:1px solid #DEE2E6; border-radius:8px; padding:18px; box-shadow:0 1px 3px rgba(0,0,0,.04); }
.lm-card-title { font-size:14px; font-weight:700; color:#0D1B2A; margin-bottom:10px; }
.lm-form-row { display:flex; gap:10px; align-items:flex-end; flex-wrap:wrap; margin-bottom:14px; }
.lm-fg { display:flex; flex-direction:column; gap:4px; }
.lm-fl { font-size:11px; font-weight:500; color:#6C757D; }
.lm-inp, .lm-sel { background:#F8F9FA; border:1px solid #CED4DA; color:#0D1B2A; font-family:'Inter',sans-serif; font-size:13px; padding:7px 10px; border-radius:6px; outline:none; }
.lm-inp:focus, .lm-sel:focus { border-color:#1565C0; box-shadow:0 0 0 3px rgba(37,99,235,.1); }
.lm-btn { padding:8px 16px; border:none; border-radius:6px; font-size:13px; font-weight:700; cursor:pointer; }
.lm-btn-p { background:#6366f1; color:#fff; }
.lm-btn:disabled { opacity:.5; cursor:default; }
.lm-sec-hdr { font-size:12px; font-weight:700; color:#495057; margin-bottom:8px; padding-bottom:5px; border-bottom:1px solid #DEE2E6; }
.lm-tbl { width:100%; border-collapse:separate; border-spacing:0; font-size:13px; border:1px solid #DEE2E6; border-radius:7px; overflow:hidden; }
.lm-tbl th { background:#F8F9FA; padding:8px 12px; text-align:left; font-size:11px; font-weight:700; color:#6C757D; text-transform:uppercase; border-bottom:2px solid #DEE2E6; }
.lm-tbl td { padding:7px 12px; border-bottom:1px solid #DEE2E6; color:#0D1B2A; }
.lm-tbl tr:last-child td { border-bottom:none; }
.lm-pill { display:inline-flex; align-items:center; padding:2px 8px; border-radius:20px; font-size:11px; font-weight:700; }
.lm-empty { text-align:center; padding:32px; color:#ADB5BD; font-size:13px; }
.lm-stats { display:flex; background:#fff; border:1px solid #DEE2E6; border-radius:8px; overflow:hidden; margin-bottom:14px; }
.lm-stat-b { flex:1; padding:11px 16px; border-right:1px solid #DEE2E6; }
.lm-stat-b:last-child { border-right:none; }
.lm-stat-l { font-size:10px; color:#6C757D; font-weight:600; text-transform:uppercase; letter-spacing:.4px; margin-bottom:2px; }
.lm-stat-v { font-size:20px; font-weight:700; font-family:'JetBrains Mono',monospace; line-height:1.1; }
.lm-stat-s { font-size:11px; color:#6C757D; margin-top:2px; }
`;
